/**
 * Contains contexts and provider for what is "currently selected" (i.e current view, object type id, etc.)
 */

import { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { clientStorage as defaultClientStorage } from './client-storage';
import { truncateOldHashes } from '../util/storageMangager';
import { pageTypeVar } from '../reactiveVar/currentPageType';
import { normalizeVizType } from '../model/vizSetting';

/**
 * The context for the current draft.
 * Used in identifying the current view and object type, and the edit hash.
 */

/**
 * Stable context that rarely changes - contains registry, storage, etc.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const VizCoreStableContext = /*#__PURE__*/createContext({
  clientStorage: defaultClientStorage,
  vizRegistry: {},
  onDraftChange: () => {},
  bypassStorage: true,
  getScopes: () => new Set()
});

/**
 * Volatile context that changes frequently - only the draft state
 */
const VizCoreDraftContext = /*#__PURE__*/createContext({
  viewId: 'all',
  objectTypeId: '0-1',
  editHash: null
});
const EMPTY_OBJECT = {};
const defaultGetScopes = () => new Set();
export function VizCoreProvider({
  children,
  clientStorage = defaultClientStorage,
  vizRegistry = EMPTY_OBJECT,
  bypassStorage = true,
  getScopes = defaultGetScopes,
  pageType,
  // @ts-expect-error - I know what I'm doing
  draftContext,
  // @ts-expect-error - I know what I'm doing
  onDraftChange,
  // @ts-expect-error - I know what I'm doing
  currentViewId,
  // @ts-expect-error - I know what I'm doing
  objectTypeId,
  // @ts-expect-error - I know what I'm doing
  editHash,
  // @ts-expect-error - I know what I'm doing
  onEditHashChange
}) {
  const onDraftChangeMemo = useMemo(() => {
    if (draftContext) {
      return onDraftChange !== null && onDraftChange !== void 0 ? onDraftChange : () => {};
    }
    return ctx => {
      if (ctx.viewId === currentViewId && ctx.objectTypeId === objectTypeId) {
        onEditHashChange === null || onEditHashChange === void 0 || onEditHashChange(ctx.editHash);
      }
    };
  }, [currentViewId, objectTypeId, onEditHashChange, draftContext, onDraftChange]);
  const stableValue = useMemo(() => {
    return {
      clientStorage,
      vizRegistry,
      onDraftChange: onDraftChangeMemo,
      bypassStorage,
      getScopes
    };
  }, [clientStorage, vizRegistry, onDraftChangeMemo, bypassStorage, getScopes]);
  const draftValue = useMemo(() => {
    if (draftContext) {
      return draftContext;
    }
    return {
      viewId: currentViewId,
      objectTypeId,
      editHash
    };
  }, [draftContext, currentViewId, objectTypeId, editHash]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      void truncateOldHashes(clientStorage);
    };
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => window.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clientStorage]);

  // Sync pageType to reactive var for global access by ViewMeta
  useEffect(() => {
    const normalizedPageType = pageType ? normalizeVizType(pageType) : null;
    pageTypeVar(normalizedPageType);
  }, [pageType]);
  return /*#__PURE__*/_jsx(VizCoreDraftContext.Provider, {
    value: draftValue,
    children: /*#__PURE__*/_jsx(VizCoreStableContext.Provider, {
      value: stableValue,
      children: children
    })
  });
}

/**
 * Hook for accessing stable context (won't re-render when draft changes)
 */
export function useVizCoreStable() {
  const context = useContext(VizCoreStableContext);
  if (!context) {
    throw new Error('useVizCoreStable must be used within a VizCoreProvider');
  }
  return context;
}

/**
 * Returns the visualization registry from the current context.
 * Now optimized - won't re-render when draft context changes!
 */
export function useVisualizationRegistry() {
  const {
    vizRegistry
  } = useVizCoreStable();
  return vizRegistry;
}

/**
 * Returns the current view ID.
 * Prefer using `useDraftContext` instead.
 *
 * @throws {Error} If used outside of a VizCoreProvider
 */
export function useCurrentViewId() {
  const [{
    viewId
  }] = useDraftContext();
  return viewId;
}

/**
 * Returns the current object type ID.
 * Prefer using `useDraftContext` instead.
 * @throws {Error} If used outside of a VizCoreProvider
 */
export function useCurrentObjectType() {
  const [{
    objectTypeId
  }] = useDraftContext();
  return objectTypeId;
}

/**
 * Returns the current edit hash and a function to update it.
 * @deprecated Consider using the host app's multi-view draft management instead.
 */
export function useEditHash() {
  const [draftContext, onDraftChange] = useDraftContext();
  const onChange = useCallback(editHash => {
    onDraftChange(Object.assign({}, draftContext, {
      editHash
    }));
  }, [draftContext, onDraftChange]);
  return [draftContext.editHash, onChange];
}

/**
 * Returns the current draft context.
 * Now optimized to use separate contexts!
 */
export function useDraftContext() {
  const draftContext = useContext(VizCoreDraftContext);
  if (!draftContext) {
    throw new Error('useDraftContext must be used within a VizCoreProvider');
  }
  const {
    onDraftChange
  } = useVizCoreStable();
  return useMemo(() => [draftContext, onDraftChange], [draftContext, onDraftChange]);
}

/**
 * Returns the client storage instance.
 * Now optimized - won't re-render when draft context changes!
 */
export function useClientStorage() {
  const {
    clientStorage
  } = useVizCoreStable();
  return clientStorage;
}

/**
 * Returns whether a specific visualization type is available in the current registry.
 * Now optimized - won't re-render when draft context changes!
 */
export function useIsVizPartAvailable(vizOrPageType, part) {
  var _vizRegistry$vizType;
  const vizRegistry = useVisualizationRegistry();
  if (!vizOrPageType) {
    return false;
  }
  const vizType = normalizeVizType(vizOrPageType);
  return Boolean((_vizRegistry$vizType = vizRegistry[vizType]) === null || _vizRegistry$vizType === void 0 ? void 0 : _vizRegistry$vizType[part]);
}

/**
 * Returns the bypass storage setting from the current context.
 * When true, hooks will skip persistent storage operations for better performance.
 * @throws {Error} If used outside of a VizCoreProvider
 */
export function useBypassStorage() {
  const {
    bypassStorage
  } = useVizCoreStable();
  return bypassStorage;
}
export function useGetScopes() {
  const {
    getScopes
  } = useVizCoreStable();
  return getScopes;
}